import React, { useCallback, useEffect, useRef } from 'react';

/**
 * The light table: one record's whole roll, current frame large, the rest as a
 * strip underneath. Two photos or twenty use the same component.
 */
export default function LightTable({ photos, index, caption, onNavigate, onClose }) {
  const closeRef = useRef(null);
  const restoreRef = useRef(null);

  useEffect(() => {
    restoreRef.current = document.activeElement;
    if (closeRef.current) closeRef.current.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      if (restoreRef.current && restoreRef.current.focus) restoreRef.current.focus();
    };
  }, []);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNavigate(1);
      if (e.key === 'ArrowLeft') onNavigate(-1);
    },
    [onClose, onNavigate]
  );

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  if (!photos.length) return null;

  return (
    <div
      className="lt"
      role="dialog"
      aria-modal="true"
      aria-label={caption}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="lt-panel">
        <div className="lt-plate">
          <img src={photos[index]} alt={`${caption} — ${index + 1} of ${photos.length}`} />
        </div>

        <div className="lt-bar">
          <span className="cap">{caption}</span>
          {/* One photo needs no counter and no arrows — a "1 / 1" between two
              dead chevrons is three controls saying nothing. */}
          {photos.length > 1 && (
            <span>
              <button className="lt-nav" onClick={() => onNavigate(-1)} aria-label="Previous photo">
                ‹
              </button>
              <span>{index + 1} / {photos.length}</span>
              <button className="lt-nav" onClick={() => onNavigate(1)} aria-label="Next photo">
                ›
              </button>
            </span>
          )}
          <button className="lt-x" ref={closeRef} onClick={onClose}>
            Close · esc
          </button>
        </div>

        {photos.length > 1 && (
          <div className="lt-thumbs">
            {photos.map((src, n) => (
              <button
                key={src}
                aria-current={n === index ? 'true' : 'false'}
                aria-label={`Photo ${n + 1}`}
                onClick={() => onNavigate(n - index)}
              >
                <img src={src} alt="" loading="lazy" decoding="async" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
